import { useEffect, useMemo, useState } from 'react';

import { padColumns } from 'utils/pad-columns';

import type { CreditorWithDetails } from 'api';
import { useGetCustomerCreditors } from 'api';

import { useOrganisationId } from 'hooks/context';
import { useHandleRowClick } from 'hooks/useHandleRowClick';
import { useVirtualList } from 'hooks/useVirtualList';

import { DataTable } from 'components/ui/organisms/DataTable';

import { apiParams } from './apiAdapter';
import { getColumns } from './columns';
import {
  RECEIVERS_TABLE_TEST_ID,
  RECEIVERS_TABLE_ROW_ENTRY_TEST_ID,
  RECEIVERS_TABLE_TOTAL_TEST_ID
} from './constants';
import { useLocalState } from './localState';
import { ReceiverDetails } from './ReceiverDetails/ReceiverDetails';

export const ReceiversTable = () => {
  const { state: localState, updateCursorPrev } = useLocalState();
  const [paginationTotal, setPaginationTotal] = useState<number>(0);

  const organisationId = useOrganisationId<number>();

  const { data: receivers, isFetching } = useGetCustomerCreditors(
    organisationId,
    apiParams({ ...localState })
  );

  const receiversColumns = useMemo(() => padColumns(getColumns()), []);

  const { onScroll, data, removeElement } = useVirtualList<CreditorWithDetails>(
    {
      data: receivers?.data,
      uniqueKey: 'id',
      isFetching,
      initialRefValue:
        receivers?.meta?.cursor_next !== null &&
        receivers?.meta?.cursor_next !== undefined
          ? String(receivers?.meta?.cursor_next)
          : undefined,
      updateCursorCallback: updateCursorPrev,
      resetConditions: [updateCursorPrev],
      cursorNext: receivers?.meta?.has_next_page
        ? receivers?.meta?.cursor_next !== null &&
          receivers?.meta?.cursor_next !== undefined
          ? String(receivers?.meta?.cursor_next)
          : undefined
        : undefined
    }
  );

  useEffect(() => {
    if (receivers?.meta?.total) setPaginationTotal(receivers?.meta?.total);
  }, [receivers]);

  const [selectedRowData, setSelectedRowData] = useState<CreditorWithDetails>();
  const handleRowClick =
    useHandleRowClick<CreditorWithDetails>(setSelectedRowData);

  const handleRemoveElement = (elem: CreditorWithDetails) => {
    removeElement(elem);
    setPaginationTotal((prevAmount) => prevAmount - 1);
  };

  return (
    <DataTable
      total={paginationTotal}
      totalDataTestId={RECEIVERS_TABLE_TOTAL_TEST_ID}
      dataSource={data}
      columns={receiversColumns}
      onScroll={onScroll}
      onRowClick={handleRowClick}
      isLoading={isFetching}
      rowKey="id"
      selectedRow={selectedRowData}
      tableDataTestId={RECEIVERS_TABLE_TEST_ID}
      rowDataTestId={RECEIVERS_TABLE_ROW_ENTRY_TEST_ID}
      rightPanel={
        selectedRowData ? (
          <ReceiverDetails
            receiverId={selectedRowData.id}
            onClose={() => setSelectedRowData(undefined)}
            onDelete={() => handleRemoveElement(selectedRowData)}
          />
        ) : undefined
      }
    />
  );
};
